import { createSelector } from 'reselect';
import { fromJS } from 'immutable';

const defaultLinks = [];
const defaultMenu = [];

const getLanguage = (props) => props.languageData || fromJS({});

const getHideDefaultLinks = (props) => props.hideDefaultLinks;

export const getUserLinks = (props) => props.links || defaultLinks;
const getUserMenu = (props) => props.menu;
const getShowMenu = (props) => props.showMenu || null;
const getShowLinks = (props) => props.showLinks || null;

export const getCorrelationId = (state) => state.getIn(['modules', 'one-identity-root', 'core', 'oneIdentityCorrelationId']);

export const getImmutableAuthState = (state) => state.getIn(['modules', 'one-identity-root', 'auth']);

export const getInauthScriptDomain = (state) => state.getIn(['config', 'inauthScriptDomain']);
export const getDefaultDestination = (props, state) => state.getIn(['config', 'globalDashboardUrl']);
export const getLogSuccess = (state) => state.getIn(['config', 'fetchLogging', 'one-identity-login']);
export const getRecaptchKey = (state) => state.getIn(['config', 'recaptchaSiteKey']);
export const getUserId = (state) => state.getIn(['modules', 'one-identity-login', 'userId']);

export const getNormalizedQuery = (query) => {
  const result = {};
  fromJS(query).entrySeq().forEach(([key, value]) => {
    result[key.toLowerCase()] = value;
  });
  return result;
};

export const getFuidUserId = createSelector(
  [getImmutableAuthState],
  (state) => state.get('userId')
);

export const getAuthLoginUrl = createSelector(
  [getImmutableAuthState],
  (state) => state.get('loginUrl')
);

export const getAuthLoginErrorCode = createSelector(
  [getImmutableAuthState],
  (state) => state.getIn(['status', 'errorCode'], '')
);

export const getAuthLoginStatusCode = createSelector(
  [getImmutableAuthState],
  (state) => state.getIn(['status', 'statusCode'], 2)
);

export const getDestination = (state) => state.getIn(['modules', 'one-identity-login', 'destination'], fromJS({}));

const getLinks = createSelector(
  [getLanguage],
  (language) => language.links || {}
);

export const getSSOLoginV2 = createSelector(
  [getLinks],
  (links) => links.ssoLoginV2
);

export const getSSOLoginV4 = createSelector(
  [getLinks],
  (links) => links.ssoLoginV4
);

const getSecondaryLinks = createSelector(
  [getLinks],
  (links) => links.secondary || []
);

const getPrimaryMenu = createSelector(
  [getLinks],
  (links) => links.menu || []
);

function compareFn(obj) {
  return (a, b) => obj.indexOf(a.key) - obj.indexOf(b.key);
}

export const getEffectiveKey = createSelector(
  // props argument needed because of the getLoginLinks selector
  [(props, state) => getDestination(state)],
  (keyObj) => (keyObj && (keyObj.overrideKey || keyObj.key)) || 'account'
);

export const getLoginLinks = createSelector(
  [getLanguage, getSecondaryLinks, getUserLinks,
    getHideDefaultLinks, getShowLinks, getEffectiveKey],
  // eslint-disable-next-line max-params -- disabling max params
  (lang, secondary, userLinks, hideDefault, showLinks, effectiveKey) => {
    const userObject = {};
    userLinks.forEach((link) => {
      userObject[link.key] = link;
    });
    const secObject = {};
    secondary.forEach((link) => {
      secObject[link.key] = link;
    });
    const linkObject = { ...secObject, ...userObject };
    const links = Object.values(hideDefault ? userObject : linkObject);

    return links
      .filter((link) => showLinks === null || showLinks.includes(link.key))
      .sort(compareFn(showLinks || [...Object.keys(links)]))
      .map((link) => ({
        key: link.key,
        label: link.label || lang[link.key],
        url: typeof link.url === 'object' ? link.url[effectiveKey] || link.url.account : link.url,
        overrideKey: effectiveKey,
        element: link.element || link.key,
        title: link.title || link.label || lang[link.key],
      }));
  }
);

// normalize user menu with built-in menu
export const getLoginMenu = createSelector(
  [getLanguage, getPrimaryMenu, getUserMenu, getShowMenu, getDefaultDestination],
  // eslint-disable-next-line max-params -- disabling max params
  (lang, primary, userMenu, showMenu, defaultDestination) => {
    const menu = [...primary, ...userMenu || defaultMenu];

    return menu
      .filter((menuItem) => showMenu === null || showMenu.includes(menuItem.key))
      .sort(compareFn(showMenu || [...Object.keys(menu)]))
      .map((menuItem) => ({
        key: menuItem.key,
        overrideKey: menuItem.overrideKey,
        value: menuItem.key,
        url: menuItem.url ?? defaultDestination,
        sso: menuItem.sso,
        brandname: menuItem.brandname,
        label: menuItem.label || lang[menuItem.key],
      }));
  }
);
